import { RiskyDisclosureParagraph } from '@/components/common/DisclosureParagraph';
import { InfoButtonWithModal } from '@/components/common/InfoModal';
import { RadioInputLabel } from '@/components/common/RadioInput';
import { RadioInputGroupProps } from '@/components/common/RadioInputGroup';
import { Paragraph } from '@/components/common/Typography';
import IdvIntro from '@/components/idv/IdvIntro';
import { useTrackPageViewed } from '@/services/analytics/useTracking';
import { Box } from '@chakra-ui/react';
import { DOWNTURN_REACTION_OPTIONS } from '@common/inputs';
import { InvestmentProfile } from '@common/types';
import { DownturnChart } from '../chart-images';
import { IdvForm } from '../IdvForm';

type T = { downturnReaction?: InvestmentProfile['downturn_reaction'] };

const options: RadioInputGroupProps['options'] = DOWNTURN_REACTION_OPTIONS.map(
    ({ label, description, value }) => ({
        label: (
            <RadioInputLabel key={value} label={label}>
                <Paragraph size="sm" color="gray" position="relative">
                    <Box
                        as="span"
                        position="absolute"
                        top={0}
                        bottom={0}
                        left={-2}
                        right={-2}
                        bg="white"
                        zIndex={-1}
                    />
                    {description}
                </Paragraph>
                <DownturnChart
                    right={{ SELL: '166px', HOLD: '66px', BUY: '-33px' }[value]}
                />
            </RadioInputLabel>
        ),
        value,
    })
);

export const DownturnReactionView = () => {
    useTrackPageViewed('IDV > DownturnReactionView');

    return (
        <IdvForm<T>
            initValues={(userProfile) => ({
                downturnReaction:
                    userProfile?.investmentProfile?.downturn_reaction ||
                    undefined,
            })}
            toMutationInput={({ downturnReaction }) => ({
                investmentProfile: { downturn_reaction: downturnReaction },
            })}
            backNextNavProps={{
                onClickEventParams: { location: 'IDV > DownturnReactionView' },
            }}
        >
            <IdvIntro
                title={
                    <>
                        If an investment dropped in value, what would you do?
                        <InfoButtonWithModal
                            title="If your investment portfolio dropped in value, what would you do?"
                            buttonProps={{
                                position: 'relative',
                                top: '-1px',
                                onClickEventParams: {
                                    location: 'IDV > DownturnReactionView',
                                },
                            }}
                            color="gray"
                        >
                            <Paragraph>
                                We ask this to understand how reactive you are
                                as an investor.
                            </Paragraph>
                            <Paragraph>
                                When you choose to sell off your stocks, that
                                suggests that you prefer more stable
                                investments. We’ll create a lower risk portfolio
                                that is less likely to go down. That means
                                you’ll hold more bonds and treasuries.
                            </Paragraph>
                            <Paragraph>
                                When you choose to buy more stocks, we’ll create
                                a higher risk portfolio that may go up (or down)
                                more. That means you’ll hold more US and
                                international stocks.
                            </Paragraph>
                        </InfoButtonWithModal>
                    </>
                }
                desc="We ask this to understand how reactive you are as an investor and factor this into portfolio recommendations."
            />
            <IdvForm.Input<T>
                name="downturnReaction"
                label=""
                type="radio"
                options={options}
                radioProps={{ mb: 1 }}
                isRequired
                autoFocus
            />
            <RiskyDisclosureParagraph mt={8} />
        </IdvForm>
    );
};
